import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Layouts
import AdminLayout from './components/layout/AdminLayout';
import UserLayout from './components/layout/UserLayout';

// Auth Pages
import LoginPage from './features/auth/LoginPage';
import RegisterPage from './features/auth/RegisterPage';

// Admin Features
import DashboardPage from './features/dashboard/DashboardPage';
import DevicesPage from './features/devices/DevicesPage';
import DeviceDetailPage from './features/devices/DeviceDetailPage';
import LicensesPage from './features/licenses/LicensesPage';
import PackagesPage from './features/licenses/PackagesPage';
import GlobalSettingsPage from './features/settings/GlobalSettingsPage';
import CouponsPage from './features/commerce/CouponsPage';
import CustomersPage from './features/staff/CustomersPage';
import OrdersPage from './features/commerce/OrdersPage';
import AuditLogsPage from './features/system/AuditLogsPage';
import AccessControlPage from './features/settings/AccessControlPage';
import CmsPage from './features/cms/CmsPage';
import ContactMessagesPage from './features/support/ContactMessagesPage';
import TicketManagerPage from './features/support/TicketManagerPage';

import UserLicensesPage from './features/dashboard/UserLicensesPage';
import UserOrdersPage from './features/dashboard/UserOrdersPage';
import UserProfilePage from './features/dashboard/UserProfilePage';
import StaffDashboard from './features/dashboard/StaffDashboard';
import UserSettingsPage from './features/dashboard/UserSettingsPage';

// Staff Management
import StaffPage from './features/staff/StaffPage';
import RolesPage from './features/staff/RolesPage';

// User Features
import UserDashboard from './features/dashboard/UserDashboard';
import UserTicketsPage from './features/support/UserTicketsPage';
import UserPackagesPage from './features/dashboard/UserPackagesPage';

// Public CMS Features
import PublicPageViewer from './features/cms/PublicPageViewer';
import { PublicHomeWrapper } from './features/cms/PublicHomeWrapper';

import SystemHealthPage from './features/system/SystemHealthPage';
import DatabasePage from './features/system/DatabasePage';
import AgentDebugPage from './features/system/AgentDebugPage';

import EmailTemplates from './features/settings/EmailTemplates';

import PublicBlogPage from './features/cms/PublicBlogPage';
import BlogPost from './features/cms/BlogPost';
import DownloadHub from './features/dashboard/DownloadHub';

import ReleaseManagerPage from './features/system/ReleaseManagerPage';
import ChangelogPage from './features/cms/ChangelogPage';

import PublicDocsPage from './features/cms/PublicDocsPage';
import ContactPage from './features/cms/ContactPage';
import PublicDownloadPage from './features/cms/PublicDownloadPage';

import ProductShowcase from './features/cms/ProductShowcase';
import PricingSection from './features/cms/PricingSection';
import FleetBehaviorPage from './features/dashboard/FleetBehaviorPage';

import DetcctionAlertsPage from './features/alerts/DetcctionAlertsPage';
import SocHistoryPage from './features/system/SocHistoryPage';
import IntruderMapPage from './features/dashboard/IntruderMapPage';
import ForensicLedgerPage from './features/dashboard/ForensicLedgerPage';
import FleetWarMap from './features/dashboard/FleetWarMap';
import AutonomousSwarmPage from './features/swarm/AutonomousSwarmPage';
import WebAuditPage from './features/dashboard/WebAuditPage';
import NeuralPulsePage from './features/dashboard/NeuralPulsePage';

// --- DOMAIN UTILS ---
const getAppMode = () => {
    const host = window.location.hostname;
    const search = window.location.search;

    // Force Preview mode if URL contains ?preview=true
    if (search.includes('preview=true')) return 'admin';

    if (host.includes('corecontrol')) return 'admin';
    if (host.includes('account')) return 'customer';
    
    // Exact match for production domains
    if (host === 'airssecurity.com' || host === 'www.airssecurity.com') return 'public';
    
    // Dev environment fallback
    return 'admin'; 
};

// --- PROTECTED ROUTE WRAPPER ---
const ProtectedRoute = ({ children, requiredRole, requiredPermission }) => {
    const { user, isAuthenticated, loading } = useAuth();
    const mode = getAppMode();

    if (loading) return <div className="h-screen flex items-center justify-center text-slate-500 font-mono">Initializing Secure Handshake...</div>;
    if (!isAuthenticated) return <Navigate to="/login" replace />;

    const userRole = user?.role;

    // 1. Domain/Role Mismatch Check
    if (mode === 'customer' && userRole !== 'customer') return <Navigate to="/login" replace />;
    if (mode === 'admin' && userRole === 'customer') return <Navigate to="/login" replace />;

    // 2. Super Admin Bypass (God Mode)
    if (userRole === 'super_admin') return children;

    // 3. Role Check (Legacy Support)
    if (requiredRole && userRole !== requiredRole) return <Navigate to="/" replace />;

    // 4. Permission Check
    if (requiredPermission) {
        const hasPermission = user?.permissions?.includes(requiredPermission);
        if (!hasPermission) return <Navigate to="/" replace />;
    }

    return children;
};

// --- DASHBOARD SWITCHER ---
const DashboardSwitcher = () => {
    const { user } = useAuth();
    if (user?.role === 'super_admin') return <DashboardPage />;
    return <StaffDashboard />;
};

function App() {
    const [mode, setMode] = useState(getAppMode());

    useEffect(() => { setMode(getAppMode()); }, []);

    return (
    <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* --- MODE A: ADMIN PANEL --- */}
        {mode === 'admin' && (
            <>
                <Route path="/" element={<ProtectedRoute><AdminLayout><DashboardSwitcher /></AdminLayout></ProtectedRoute>} />
                <Route path="/alerts/threat-detections" element={<ProtectedRoute requiredPermission="alerts:view"><AdminLayout><DetcctionAlertsPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/behavior" element={<ProtectedRoute requiredPermission="devices:view"><AdminLayout><FleetBehaviorPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/soc-ledger" element={<ProtectedRoute><AdminLayout><SocHistoryPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/devices" element={<ProtectedRoute requiredPermission="devices:view"><AdminLayout><DevicesPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/devices/:agentId" element={<ProtectedRoute requiredPermission="devices:view"><AdminLayout><DeviceDetailPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/licenses" element={<ProtectedRoute requiredPermission="licenses:view"><AdminLayout><LicensesPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/packages" element={<ProtectedRoute requiredPermission="packages:view"><AdminLayout><PackagesPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/customers" element={<ProtectedRoute requiredPermission="customers:view"><AdminLayout><CustomersPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute requiredPermission="orders:view"><AdminLayout><OrdersPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/coupons" element={<ProtectedRoute requiredPermission="coupons:view"><AdminLayout><CouponsPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/staff" element={<ProtectedRoute requiredPermission="staff:manage"><AdminLayout><StaffPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/roles" element={<ProtectedRoute requiredPermission="staff:manage"><AdminLayout><RolesPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/tickets" element={<ProtectedRoute requiredPermission="tickets:view"><AdminLayout><TicketManagerPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/contact-messages" element={<ProtectedRoute requiredPermission="cms:view"><AdminLayout><ContactMessagesPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/cms" element={<ProtectedRoute requiredPermission="cms:view"><AdminLayout><CmsPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/forensic-ledger" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><ForensicLedgerPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/web-auditor" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><WebAuditPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/audit-logs" element={<ProtectedRoute requiredPermission="logs:view"><AdminLayout><AuditLogsPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/intruder-map" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><IntruderMapPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/war-map" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><FleetWarMap /></AdminLayout></ProtectedRoute>} />
                <Route path="/admin/swarm" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><AutonomousSwarmPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/cognitive-pulse" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><NeuralPulsePage /></AdminLayout></ProtectedRoute>} />
                <Route path="/system-health" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><SystemHealthPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/agent-debug" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><AgentDebugPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/system/releases" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><ReleaseManagerPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/settings/email-templates" element={<AdminLayout><EmailTemplates /></AdminLayout>} />
                <Route path="/settings" element={<ProtectedRoute requiredPermission="settings:view"><AdminLayout><GlobalSettingsPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/access-control" element={<ProtectedRoute requiredPermission="settings:view"><AdminLayout><AccessControlPage /></AdminLayout></ProtectedRoute>} />
                <Route path="/database" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout><DatabasePage /></AdminLayout></ProtectedRoute>} />
                <Route path="/my-profile" element={<ProtectedRoute><AdminLayout><UserProfilePage /></AdminLayout></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </>
        )}

        {/* --- MODE B: CUSTOMER PORTAL --- */}
        {mode === 'customer' && (
            <>
                <Route path="/portal/dashboard" element={<ProtectedRoute requiredRole="customer"><UserLayout><UserDashboard /></UserLayout></ProtectedRoute>} />
                <Route path="/portal/download" element={<ProtectedRoute requiredRole="customer"><UserLayout><DownloadHub /></UserLayout></ProtectedRoute>} />
                <Route path="/portal/licenses" element={<ProtectedRoute requiredRole="customer"><UserLayout><UserLicensesPage /></UserLayout></ProtectedRoute>} />
                <Route path="/portal/orders" element={<ProtectedRoute requiredRole="customer"><UserLayout><UserOrdersPage /></UserLayout></ProtectedRoute>} />
                <Route path="/portal/profile" element={<ProtectedRoute requiredRole="customer"><UserLayout><UserProfilePage /></UserLayout></ProtectedRoute>} />
                <Route path="/portal/settings" element={<ProtectedRoute requiredRole="customer"><UserLayout><UserSettingsPage /></UserLayout></ProtectedRoute>} />
                <Route path="/portal/tickets" element={<ProtectedRoute requiredRole="customer"><UserLayout><UserTicketsPage /></UserLayout></ProtectedRoute>} />
                <Route path="/portal/shop" element={<ProtectedRoute requiredRole="customer"><UserLayout><UserPackagesPage /></UserLayout></ProtectedRoute>} />
                <Route path="/" element={<Navigate to="/portal/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/portal/dashboard" replace />} />
            </>
        )}

        {/* --- MODE C: PUBLIC WEBSITE --- */}
        {mode === 'public' && (
            <>
                {/* 1. Main Landing */}
                <Route path="/" element={<PublicHomeWrapper />} />
                
                {/* 2. SPECIFIC FUNCTIONAL ROUTES (The Missing Link) */}
                
                <Route path="/features" element={<PublicHomeWrapper><ProductShowcase /></PublicHomeWrapper>} />
                <Route path="/pricing" element={<PublicHomeWrapper><PricingSection /></PublicHomeWrapper>} />
                
                <Route path="/docs" element={<PublicHomeWrapper><PublicDocsPage /></PublicHomeWrapper>} />
                <Route path="/download" element={<PublicHomeWrapper><PublicDownloadPage /></PublicHomeWrapper>} />
                
                <Route path="/changelog" element={<PublicHomeWrapper><ChangelogPage /></PublicHomeWrapper>} />
                <Route path="/contact" element={<PublicHomeWrapper><ContactPage /></PublicHomeWrapper>} />
                
                {/* 3. Blog Routes */}
                <Route path="/blog" element={<PublicHomeWrapper><PublicBlogPage /></PublicHomeWrapper>} />
                <Route path="/blog/:slug" element={<PublicHomeWrapper><BlogPost /></PublicHomeWrapper>} />

                {/* 4. Dynamic CMS Routes (About, Privacy, etc.) */}
                <Route path="/pages/:slug" element={<PublicHomeWrapper><PublicPageViewer /></PublicHomeWrapper>} />
                
                <Route path="/login" element={<Navigate to="https://account.airssecurity.com/login" replace />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </>
        )}
    </Routes>
);
}

export default App;